import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../ui/alert-dialog';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Loader2, Plus, Truck, AlertTriangle } from 'lucide-react';
import { QuickAddTransport } from '../ui/quick-add';

/**
 * Unified LR Report Form
 * Used by: LRReportsList (standalone), OrderDetails (embedded), LRDetails (edit)
 *
 * Status is SYSTEM-CONTROLLED and read-only:
 * - In Transport (default on creation)
 * - Issued for Delivery (set by LR delivery issue workflow)
 * - Delivered (set when grey receipt is created against LR)
 */

const LR_STATUS_COLORS = {
  'In Transport': 'bg-blue-50 text-blue-700 border-blue-200',
  'Issued for Delivery': 'bg-amber-50 text-amber-700 border-amber-200',
  'Delivered': 'bg-teal-50 text-teal-700 border-teal-200'
};

const LRReportForm = ({
  orderId = null,
  bills = [],
  initialData = null,
  onSuccess,
  onCancel,
  mode = 'create',
  hasLinkedReceipts = false,   // LR already has linked Grey Receipt(s)
  isAdmin = false              // current user is an admin
}) => {
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [transports, setTransports] = useState([]);
  const [qualities, setQualities] = useState([]);
  const [orderBills, setOrderBills] = useState(bills);
  const [showAddTransport, setShowAddTransport] = useState(false);
  const [showReceiptWarning, setShowReceiptWarning] = useState(false);
  const pendingPayload = useRef(null);
  const hasInitialized = useRef(false);

  // Non-admins cannot edit impactful fields once receipts are linked
  const lockRestricted = mode === 'edit' && hasLinkedReceipts && !isAdmin;
  const [formData, setFormData] = useState({
    order_id: orderId || '',
    bill_id: '',
    lr_number_transport: '',
    bales_in_lr: '',
    material_quantity: '',
    transport_id: '',
    lr_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchData();
    if (initialData && !hasInitialized.current) {
      hasInitialized.current = true;
      setFormData({
        order_id: orderId || initialData.order_id || '',
        bill_id: initialData.bill_id || '',
        lr_number_transport: initialData.lr_number_transport || '',
        bales_in_lr: initialData.bales_in_lr || '',
        material_quantity: initialData.material_quantity ?? '',
        transport_id: initialData.transport_id || '',
        lr_date: initialData.lr_date?.split('T')[0] || new Date().toISOString().split('T')[0]
      });
    }
  }, [initialData, mode, orderId]);

  const fetchData = async () => {
    try {
      const [ordersRes, transportsRes, qualitiesRes] = await Promise.all([
        api.get('/grey-orders'),
        api.get('/transports'),
        api.get('/qualities')
      ]);
      setOrders(ordersRes.data);
      setTransports(transportsRes.data);
      setQualities(qualitiesRes.data);

      // If orderId is preset, fetch bills for that order
      if (orderId) {
        const billsRes = await api.get(`/grey-bills?order_id=${orderId}`);
        setOrderBills(billsRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch data', error);
    }
  };

  // Derive the material unit from the selected order's Quality default_unit
  const effectiveOrderId = formData.order_id || orderId;
  const selectedOrder = orders.find(o => o.order_id === effectiveOrderId);
  const selectedQuality = selectedOrder
    ? qualities.find(q => q.quality_id === selectedOrder.quality_id)
    : null;
  const materialUnit = selectedQuality?.default_unit || selectedOrder?.order_unit || initialData?.material_unit || 'Bales';

  const handleOrderChange = async (value) => {
    setFormData(prev => ({ ...prev, order_id: value, bill_id: '' }));
    // Fetch bills filtered by selected order
    try {
      const billsRes = await api.get(`/grey-bills?order_id=${value}`);
      setOrderBills(billsRes.data);
    } catch {
      setOrderBills([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const effOrderId = formData.order_id || orderId;
    if (!effOrderId || !formData.lr_number_transport || !formData.bales_in_lr || !formData.transport_id) {
      toast.error('Please fill all required fields (including Bilty Number)');
      return;
    }
    
    const bales = parseInt(formData.bales_in_lr);
    if (!bales || bales <= 0) {
      toast.error('Bales in LR must be greater than 0');
      return;
    }

    // Material Quantity is required for NEW records; positive when provided
    const materialQty = formData.material_quantity === '' ? null : parseFloat(formData.material_quantity);
    if (mode !== 'edit' && (materialQty === null || isNaN(materialQty))) {
      toast.error('Material Quantity in LR is required');
      return;
    }
    if (materialQty !== null && (isNaN(materialQty) || materialQty <= 0)) {
      toast.error('Material Quantity must be greater than 0');
      return;
    }
    
    if (!formData.lr_number_transport.trim()) {
      toast.error('Bilty Number (Transport LR Number) is required');
      return;
    }

    const payload = {
      order_id: effOrderId,
      bill_id: formData.bill_id || null,
      lr_number_transport: formData.lr_number_transport.trim(),
      bales_in_lr: bales,
      material_quantity: materialQty,
      material_unit: materialUnit,
      transport_id: formData.transport_id,
      lr_date: formData.lr_date
    };

    // Admin editing an LR with linked receipts -> confirm via warning dialog first
    if (mode === 'edit' && hasLinkedReceipts) {
      pendingPayload.current = payload;
      setShowReceiptWarning(true);
      return;
    }

    await doSave(payload);
  };

  const doSave = async (payload) => {
    setLoading(true);
    try {
      if (mode === 'edit' && initialData?.lr_id) {
        await api.put(`/lr-reports/${initialData.lr_id}`, payload);
        toast.success('LR Report updated!');
      } else {
        await api.post('/lr-reports', payload);
        toast.success('LR Report added!');
      }
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save LR Report');
      setLoading(false);
    }
  };

  const confirmReceiptWarning = async () => {
    setShowReceiptWarning(false);
    if (pendingPayload.current) {
      await doSave(pendingPayload.current);
      pendingPayload.current = null;
    }
  };

  const currentStatus = initialData?.status || 'In Transport';

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Status Badge - Read Only */}
      {mode === 'edit' && (
        <div className="flex items-center gap-2">
          <Label className="text-slate-500">Status</Label>
          <Badge data-testid="lr-status-badge" className={`${LR_STATUS_COLORS[currentStatus] || 'bg-slate-100 text-slate-700'} pointer-events-none`}>
            {currentStatus}
          </Badge>
          <span className="text-xs text-slate-400 italic">System-controlled</span>
        </div>
      )}

      {/* Order Selection (only in standalone) */}
      {mode === 'create' && !orderId && (
        <div>
          <Label htmlFor="order_id">Order *</Label>
          <Select value={formData.order_id} onValueChange={handleOrderChange}>
            <SelectTrigger data-testid="lr-order-select" className="h-11">
              <SelectValue placeholder="Select order" />
            </SelectTrigger>
            <SelectContent>
              {orders.map((order) => (
                <SelectItem key={order.order_id} value={order.order_id}>
                  {order.order_number} - {order.supplier_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Lock notice for non-admin users when receipts are linked */}
      {lockRestricted && (
        <div data-testid="lr-locked-notice" className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>This LR has linked Grey Receipt(s). Order, Bill, Bales and Material Quantity are locked. Only an administrator can change them.</span>
        </div>
      )}

      {/* Related Bill (filtered by order) */}
      {orderBills.length > 0 && (
        <div>
          <Label htmlFor="bill_id">Related Bill (optional)</Label>
          <Select value={formData.bill_id} onValueChange={(value) => setFormData({ ...formData, bill_id: value })} disabled={lockRestricted}>
            <SelectTrigger data-testid="lr-bill-select" className="h-11">
              <SelectValue placeholder="Select related bill" />
            </SelectTrigger>
            <SelectContent>
              {orderBills.map((bill) => (
                <SelectItem key={bill.bill_id} value={bill.bill_id}>
                  {bill.purchase_bill_number} - {bill.party_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div>
        <Label htmlFor="lr_number_transport">LR / Bilty Number *</Label>
        <Input
          id="lr_number_transport"
          data-testid="lr-number-input"
          value={formData.lr_number_transport}
          onChange={(e) => setFormData({ ...formData, lr_number_transport: e.target.value })}
          placeholder="TRN-2026-001"
          required
          className="h-11"
        />
      </div>

      <div>
        <Label htmlFor="bales_in_lr">Bales in LR *</Label>
        <Input
          id="bales_in_lr"
          data-testid="bales-input"
          type="number"
          value={formData.bales_in_lr}
          onChange={(e) => setFormData({ ...formData, bales_in_lr: e.target.value })}
          placeholder="10"
          required
          disabled={lockRestricted}
          className={`h-11 ${lockRestricted ? 'bg-slate-100 cursor-not-allowed' : ''}`}
        />
      </div>

      {/* Material Quantity + auto-derived Unit */}
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <Label htmlFor="material_quantity">Material Quantity in LR {mode !== 'edit' && '*'}</Label>
          <Input
            id="material_quantity"
            data-testid="material-quantity-input"
            type="number"
            step="0.01"
            min="0"
            value={formData.material_quantity}
            onChange={(e) => setFormData({ ...formData, material_quantity: e.target.value })}
            placeholder="e.g. 65"
            disabled={lockRestricted}
            className={`h-11 ${lockRestricted ? 'bg-slate-100 cursor-not-allowed' : ''}`}
          />
        </div>
        <div>
          <Label htmlFor="material_unit">Unit</Label>
          <Input
            id="material_unit"
            data-testid="material-unit-display"
            value={materialUnit}
            readOnly
            disabled
            className="h-11 bg-slate-100 text-slate-600 cursor-not-allowed"
            title="Auto-set from the Quality's default unit"
          />
        </div>
      </div>
      <p className="text-xs text-slate-500 -mt-2">Unit is auto-set from the selected Quality's default unit and cannot be changed.</p>

      <div>
        <Label htmlFor="transport_id" className="flex items-center justify-between">
          <span>Transport *</span>
          <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs text-blue-600 hover:text-blue-700" onClick={() => setShowAddTransport(true)}>
            <Plus className="h-3 w-3 mr-1" />Add New
          </Button>
        </Label>
        <Select
          value={formData.transport_id}
          onValueChange={(value) => setFormData({ ...formData, transport_id: value })}
          required
        >
          <SelectTrigger data-testid="transport-select" className="h-11">
            <SelectValue placeholder="Select transport" />
          </SelectTrigger>
          <SelectContent>
            {transports.map((transport) => (
              <SelectItem key={transport.transport_id} value={transport.transport_id}>
                {transport.transport_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <QuickAddTransport
          open={showAddTransport}
          onOpenChange={setShowAddTransport}
          onCreated={(newTransport) => {
            setTransports(prev => [...prev, newTransport]);
            setFormData(prev => ({ ...prev, transport_id: newTransport.transport_id }));
          }}
        />
      </div>

      <div>
        <Label htmlFor="lr_date">LR Date *</Label>
        <Input
          id="lr_date"
          data-testid="lr-date-input"
          type="date"
          value={formData.lr_date}
          onChange={(e) => setFormData({ ...formData, lr_date: e.target.value })}
          required
          className="h-11"
        />
      </div>

      <div className="pt-4 flex gap-2">
        <Button
          data-testid="submit-lr-btn"
          type="submit"
          disabled={loading}
          className="flex-1 bg-slate-900 hover:bg-slate-800 h-11"
        >
          {loading ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{mode === 'edit' ? 'Saving...' : 'Adding...'}</>
          ) : (
            <><Truck className="mr-2 h-4 w-4" />{mode === 'edit' ? 'Save Changes' : 'Add LR Report'}</>
          )}
        </Button>
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} className="h-11">Cancel</Button>
        )}
      </div>

      {/* Admin warning before editing an LR that has linked Grey Receipts */}
      <AlertDialog open={showReceiptWarning} onOpenChange={setShowReceiptWarning}>
        <AlertDialogContent data-testid="lr-receipt-warning-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="h-5 w-5" /> Warning
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-slate-600">
                <p>This LR is already linked with Grey Receipt(s).</p>
                <p>Changing this LR may affect:</p>
                <ul className="list-disc pl-5 space-y-0.5">
                  <li>Received Calculations</li>
                  <li>Pending Calculations</li>
                  <li>Dashboard Figures</li>
                  <li>Reports</li>
                </ul>
                <p className="font-medium text-slate-700">Do you want to continue?</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="lr-warning-cancel-btn" onClick={() => { pendingPayload.current = null; }}>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="lr-warning-confirm-btn" onClick={confirmReceiptWarning} className="bg-slate-900 hover:bg-slate-800">
              Update LR
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </form>
  );
};

export default LRReportForm;
